import NightsStayOutlinedIcon from "@mui/icons-material/NightsStayOutlined";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import WbTwilightIcon from "@mui/icons-material/WbTwilight";
import { Box, Chip, Typography } from "@mui/material";
import { type FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertCard } from "./AlertCard";
import { ProblemsCard } from "./ProblemsCard";
import type { AlertSeverity, DepartmentAlertShiftTypeView } from "./types";
import {
	computeSeverityCounts,
	getSeverityRank,
	getShiftCategory,
	isProblemsAlert,
	ROLE_GROUP_CHIP_COLORS,
	SEVERITY_COLORS,
	SEVERITY_ICONS,
	SHIFT_TYPE_COLORS,
} from "./utils";

type Props = {
	shiftType: DepartmentAlertShiftTypeView;
};

const SHIFT_ICONS = {
	Day: WbSunnyOutlinedIcon,
	Evening: WbTwilightIcon,
	Night: NightsStayOutlinedIcon,
} as const;

export const ShiftTypeColumn: FC<Props> = memo(({ shiftType: st }) => {
	const { t } = useTranslation();
	const category = getShiftCategory(st.startTime);
	const shiftColors = SHIFT_TYPE_COLORS[category];
	const ShiftIcon = SHIFT_ICONS[category];
	const shiftSevCounts = useMemo(() => computeSeverityCounts([st]), [st]);

	const sortedRoleGroups = useMemo(() => [...st.roleGroups].sort((a, b) => a.roleGroup.localeCompare(b.roleGroup)), [st.roleGroups]);

	return (
		<Box
			sx={{
				flex: "1 1 0",
				minWidth: 280,
				borderRadius: "8px",
				border: "1px solid",
				borderColor: `${shiftColors.text}30`,
				overflow: "hidden",
			}}
		>
			{/* Shift type column header */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					bgcolor: shiftColors.bg,
					px: 2,
					py: 1,
					borderBottom: "1px solid",
					borderColor: `${shiftColors.text}20`,
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					<ShiftIcon sx={{ fontSize: 18, color: shiftColors.text }} />
					<Typography
						variant="body2"
						color={shiftColors.text}
						sx={{
							fontWeight: 600,
						}}
					>
						{t(`alerts.${category.toLowerCase()}.shift`)}
					</Typography>
					<Typography variant="caption" color={shiftColors.text} sx={{ opacity: 0.7 }}>
						{st.label}
					</Typography>
				</Box>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.5,
					}}
				>
					{Object.entries(shiftSevCounts)
						.sort(([a], [b]) => getSeverityRank(b as AlertSeverity) - getSeverityRank(a as AlertSeverity))
						.map(([sev, count]) => {
							const sevColors = SEVERITY_COLORS[sev as AlertSeverity];
							const ShiftSevIcon = SEVERITY_ICONS[sev as AlertSeverity];
							return (
								<Chip
									key={sev}
									size="small"
									label={
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.5,
											}}
										>
											<ShiftSevIcon sx={{ fontSize: 10, color: sevColors.accent }} />
											{count}
										</Box>
									}
									sx={{
										bgcolor: sevColors.bg,
										color: sevColors.text,
										fontWeight: 600,
										fontSize: "0.65rem",
										height: 22,
										border: "1px solid",
										borderColor: sevColors.accent,
									}}
								/>
							);
						})}
				</Box>
			</Box>
			{/* Role groups and alerts */}
			<Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
				{sortedRoleGroups.map((rg) => (
					<Box key={rg.roleGroup}>
						<Chip
							label={rg.roleGroup}
							size="small"
							sx={{
								mb: 1,
								fontWeight: 700,
								fontSize: "0.7rem",
								textTransform: "uppercase",
								letterSpacing: 0.5,
								bgcolor: ROLE_GROUP_CHIP_COLORS.bg,
								color: ROLE_GROUP_CHIP_COLORS.text,
								borderRadius: "6px",
							}}
						/>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 1,
							}}
						>
							{rg.alerts.map((slot, idx) =>
								isProblemsAlert(slot) ? <ProblemsCard key={`problems-${idx}`} alert={slot} /> : <AlertCard key={`${slot.alertType}-${idx}`} alert={slot} />,
							)}
						</Box>
					</Box>
				))}
			</Box>
		</Box>
	);
});
